"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Separator,
  Switch,
} from "@repo/ui";
import Link from "next/link";
import { useEffect, useState } from "react";

import {
  type ConsentCategories,
  type ConsentCategory,
  defaultCategories,
} from "@/lib/cookie-consent";

import { useCookieConsent } from "./cookie-consent-context";

interface ToggleableCategory {
  key: Exclude<ConsentCategory, "necessary">;
  title: string;
  description: string;
}

// Descriptions mirror the cookie-policy.md category tables.
const TOGGLEABLE_CATEGORIES: ToggleableCategory[] = [
  {
    key: "analytics",
    title: "Analytics and performance",
    description:
      "Google Analytics, PostHog, and Datadog help us understand how the site is used so we can measure and improve it.",
  },
  {
    key: "functional",
    title: "Functional",
    description:
      "Powers our in-product support chat (Intercom) and remembers your conversation across sessions.",
  },
  {
    key: "marketing",
    title: "Marketing and advertising",
    description:
      "Google Ads measures the effectiveness of our advertising and attributes conversions from ad campaigns.",
  },
];

export function CookiePreferencesDialog() {
  const {
    isPreferencesOpen,
    closePreferences,
    choice,
    saveChoice,
    acceptAll,
    rejectAll,
  } = useCookieConsent();
  const [draft, setDraft] = useState<ConsentCategories>(defaultCategories());

  // Re-seed the toggles from the stored choice each time the dialog opens.
  useEffect(() => {
    if (isPreferencesOpen) {
      setDraft(choice?.categories ?? defaultCategories());
    }
  }, [isPreferencesOpen, choice]);

  return (
    <Dialog
      open={isPreferencesOpen}
      onOpenChange={(open) => {
        if (!open) {
          closePreferences();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            Choose which cookies Terac may use. Strictly necessary cookies are
            always on. See our{" "}
            <Link
              href="/legal/cookie-policy"
              className="text-foreground underline underline-offset-4"
            >
              Cookie Policy
            </Link>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="text-foreground">Strictly necessary</Label>
              <p className="text-muted-foreground text-sm">
                Required for sign-in, security, and core functionality. These
                cannot be turned off.
              </p>
            </div>
            <Switch
              checked
              disabled
              aria-label="Strictly necessary cookies (always on)"
            />
          </div>

          {TOGGLEABLE_CATEGORIES.map((category) => (
            <div key={category.key}>
              <Separator className="mb-4" />
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label
                    htmlFor={`consent-${category.key}`}
                    className="text-foreground"
                  >
                    {category.title}
                  </Label>
                  <p className="text-muted-foreground text-sm">
                    {category.description}
                  </p>
                </div>
                <Switch
                  id={`consent-${category.key}`}
                  checked={draft[category.key]}
                  onCheckedChange={(checked) => {
                    setDraft((prev) => ({ ...prev, [category.key]: checked }));
                  }}
                  aria-label={category.title}
                />
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={rejectAll}>
              Reject all
            </Button>
            <Button variant="outline" size="sm" onClick={acceptAll}>
              Accept all
            </Button>
          </div>
          <Button
            size="sm"
            onClick={() => {
              saveChoice(draft);
            }}
          >
            Save preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
